import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, GraduationCap, Package, Megaphone, HandCoins, MapPin, Phone, Briefcase } from 'lucide-react';
import { writePrintDoc } from '../utils/printUtils';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const dateIN = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '-';

/**
 * Per-beneficiary Livelihood report:
 *  - Header with entrepreneur photo (from Entrepreneurs) + core details
 *  - Sections for Skill Training / Products / Marketing / Financial Support
 *    each showing all fields + activity photographs
 *  - Print / Save as PDF button
 */
const EntrepreneurLivelihoodsReport = () => {
  const { entrepreneurId } = useParams();
  const navigate = useNavigate();
  const [entrepreneur, setEntrepreneur] = useState(null);
  const [branch, setBranch] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [entrepreneurId]);

  const load = async () => {
    try {
      setLoading(true);
      const [entRes, brRes, livRes] = await Promise.all([
        api.get(`/entrepreneurs/${entrepreneurId}`),
        api.get('/branches'),
        api.get(`/livelihoods?entrepreneur_id=${entrepreneurId}&include_photos=true`),
      ]);
      setEntrepreneur(entRes.data);
      setBranch((brRes.data || []).find(b => b.id === entRes.data.branch_id) || null);
      setEntries(livRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const grouped = useMemo(() => {
    const g = { skill_training: [], product: [], marketing: [], financial_support: [] };
    entries.forEach(e => { if (g[e.category]) g[e.category].push(e); });
    return g;
  }, [entries]);

  const photoSrc = entrepreneur?.photo_url
    ? (entrepreneur.photo_url.startsWith('data:')
        ? entrepreneur.photo_url
        : `${process.env.REACT_APP_BACKEND_URL}${entrepreneur.photo_url}`)
    : null;

  const handlePrint = () => {
    if (!entrepreneur) return;
    const sections = [
      { key: 'skill_training', title: '1. Skill & Job Training', rows: grouped.skill_training, keys: [
        ['training_name', 'Training'], ['institute', 'Institute'], ['training_duration', 'Duration'],
        ['training_date', 'Date', dateIN], ['training_status', 'Status'],
        ['earning_before_training', 'Earning Before', v => v ? `Rs. ${fmt(v)}` : '-'],
        ['earning_after_training', 'Earning After', v => v ? `Rs. ${fmt(v)}` : '-'],
        ['employer_name', 'Employer / Business'], ['monthly_revenue', 'Monthly Revenue', v => v ? `Rs. ${fmt(v)}` : '-'],
        ['business_address', 'Business Address'],
      ]},
      { key: 'product', title: '2. Products', rows: grouped.product, keys: [
        ['product_name', 'Product'], ['product_type', 'Type'], ['quantity', 'Quantity'],
        ['pricing', 'Pricing'], ['udyam_registration_no', 'UDYAM / MSME'], ['remarks', 'Remarks'],
      ]},
      { key: 'marketing', title: '3. Marketing & Promotion', rows: grouped.marketing, keys: [
        ['activity_name', 'Activity'], ['activity_type', 'Type'],
        ['event_date', 'Date', dateIN], ['description', 'Description'],
      ]},
      { key: 'financial_support', title: '4. Financial Support', rows: grouped.financial_support, keys: [
        ['institution_name', 'Institution'], ['institution_type', 'Type'],
        ['support_amount', 'Amount', v => v ? `Rs. ${fmt(v)}` : '-'],
        ['support_date', 'Date', dateIN], ['support_status', 'Status'],
      ]},
    ];

    const escape = (s) => String(s ?? '').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const secHtml = sections.filter(s => s.rows.length > 0).map(s => `
      <h3 style="color:#0f766e;margin-top:22px;border-bottom:2px solid #0f766e;padding-bottom:4px">${s.title} (${s.rows.length})</h3>
      ${s.rows.map((r, idx) => `
        <div style="border:1px solid #d1d5db;border-radius:6px;padding:10px;margin:8px 0;page-break-inside:avoid;background:#fafafa">
          <div style="font-weight:600;color:#0f172a;margin-bottom:6px">Entry ${idx + 1}</div>
          <table style="width:100%;border-collapse:collapse;font-size:11px">
            ${s.keys.map(([k, lbl, tx]) => `
              <tr>
                <td style="padding:3px 8px;color:#64748b;width:32%;vertical-align:top">${lbl}</td>
                <td style="padding:3px 8px;color:#0f172a;font-weight:500">${escape(tx ? tx(r[k]) : (r[k] || '-'))}</td>
              </tr>
            `).join('')}
          </table>
          ${r.photos && r.photos.length ? `
            <div style="margin-top:8px">
              <div style="font-size:10px;color:#64748b;margin-bottom:4px">Activity Photographs (${r.photos.length}):</div>
              <div style="display:flex;gap:6px;flex-wrap:wrap">
                ${r.photos.slice(0, 3).map(src => `<img src="${src}" style="width:150px;height:150px;object-fit:cover;border-radius:4px;border:1px solid #cbd5e1"/>`).join('')}
              </div>
            </div>
          ` : ''}
        </div>
      `).join('')}
    `).join('');

    const html = `<!DOCTYPE html><html><head><title>Livelihood Report — ${escape(entrepreneur.name)}</title>
<style>
  body { font-family: Arial, sans-serif; color: #0f172a; padding: 24px; }
  .hdr { display: flex; gap: 18px; border-bottom: 3px solid #0f766e; padding-bottom: 14px; margin-bottom: 18px; }
  .hdr img { width: 120px; height: 145px; object-fit: cover; border-radius: 6px; border: 2px solid #99f6e4; }
  .hdr .info h1 { margin: 0 0 4px 0; color: #0f172a; }
  .hdr .info .sub { color: #64748b; font-size: 12px; margin-bottom: 6px; }
  .hdr .info .row { font-size: 12px; margin: 2px 0; color: #334155; }
  .hdr .info .row b { color: #0f172a; }
  h3 { font-size: 15px; }
  @media print {
    body { padding: 12px; }
    .no-print { display: none; }
  }
</style>
</head><body>
  <div class="hdr">
    ${photoSrc ? `<img src="${photoSrc}" alt="${escape(entrepreneur.name)}" />` : `<div style="width:120px;height:145px;background:#e2e8f0;border-radius:6px;display:flex;align-items:center;justify-content:center;font-size:48px;color:#64748b;border:2px solid #cbd5e1">${escape(entrepreneur.name.charAt(0))}</div>`}
    <div class="info">
      <h1>Livelihood Impact Report</h1>
      <div class="sub">Wangjing Women and Girls Society — BRC MIS Portal</div>
      <div class="row"><b>${escape(entrepreneur.name)}</b> ${entrepreneur.enrollment_no ? `· ${escape(entrepreneur.enrollment_no)}` : ''}</div>
      <div class="row">${entrepreneur.gender || ''} ${entrepreneur.mobile ? `· 📞 ${escape(entrepreneur.mobile)}` : ''}</div>
      <div class="row">Branch: <b>${escape(branch?.name || entrepreneur.branch_name || '-')}</b></div>
      ${entrepreneur.address ? `<div class="row">Address: ${escape(entrepreneur.address)}</div>` : ''}
      ${entrepreneur.enterprise_name ? `<div class="row">Enterprise: <b>${escape(entrepreneur.enterprise_name)}</b> ${entrepreneur.sector ? `(${escape(entrepreneur.sector)})` : ''}</div>` : ''}
      <div class="row" style="margin-top:6px;font-size:11px;color:#64748b">Report generated: ${new Date().toLocaleString('en-IN')}</div>
    </div>
  </div>
  ${entries.length === 0 ? '<p style="color:#64748b;font-style:italic">No livelihood activities recorded yet for this beneficiary.</p>' : secHtml}
</body></html>`;

    writePrintDoc(html);
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Loading…</div>;
  if (!entrepreneur) return <div className="p-8 text-center text-slate-500">Beneficiary not found.</div>;

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="entrepreneur-livelihood-report">
      <div className="flex items-center gap-3 mb-6 print:hidden">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">Livelihood Impact Report</h1>
          <p className="text-sm text-slate-600">Consolidated view of all livelihood activities for this beneficiary.</p>
        </div>
        <Button onClick={handlePrint} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="print-livelihood-report-btn">
          <Printer className="w-4 h-4 mr-1" />Print / Save as PDF
        </Button>
      </div>

      {/* Beneficiary header card */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-6 flex flex-col md:flex-row gap-5">
        {photoSrc ? (
          <img src={photoSrc} alt={entrepreneur.name} className="w-32 h-40 rounded-lg object-cover border-2 border-emerald-100 shadow" />
        ) : (
          <div className="w-32 h-40 rounded-lg bg-slate-200 flex items-center justify-center text-5xl text-slate-500 font-bold">{entrepreneur.name.charAt(0)}</div>
        )}
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-slate-900">{entrepreneur.name}</h2>
          <div className="text-xs text-slate-500 mb-2">
            {entrepreneur.enrollment_no && <span className="mr-3">📇 {entrepreneur.enrollment_no}</span>}
            {entrepreneur.gender}
          </div>
          <div className="text-sm text-slate-700 space-y-1">
            {entrepreneur.mobile && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-emerald-600" /> {entrepreneur.mobile}</div>}
            {(branch?.name || entrepreneur.branch_name) && <div className="flex items-center gap-2"><Briefcase className="w-3.5 h-3.5 text-emerald-600" /> Branch: <b>{branch?.name || entrepreneur.branch_name}</b></div>}
            {entrepreneur.address && <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 text-emerald-600 mt-0.5" /> {entrepreneur.address}</div>}
            {entrepreneur.enterprise_name && <div>🏪 Enterprise: <b>{entrepreneur.enterprise_name}</b>{entrepreneur.sector && ` (${entrepreneur.sector})`}</div>}
          </div>
        </div>
      </div>

      {entries.length === 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-8 text-center text-slate-500 italic">No livelihood activities recorded yet for this beneficiary.</div>
      )}

      {/* Section: Skill Training */}
      {grouped.skill_training.length > 0 && (
        <SectionCard title="Skill & Job Training" count={grouped.skill_training.length} icon={GraduationCap} color="emerald">
          {grouped.skill_training.map((r, i) => (
            <EntryCard key={r.id} idx={i} fields={[
              ['Training', r.training_name], ['Institute', r.institute], ['Duration', r.training_duration],
              ['Date', dateIN(r.training_date)], ['Status', r.training_status],
              ['Earning Before', r.earning_before_training ? `Rs. ${fmt(r.earning_before_training)}` : '-'],
              ['Earning After', r.earning_after_training ? `Rs. ${fmt(r.earning_after_training)}` : '-'],
              ['Employer / Business', r.employer_name],
              ['Monthly Revenue', r.monthly_revenue ? `Rs. ${fmt(r.monthly_revenue)}` : '-'],
              ['Business Address', r.business_address],
            ]} photos={r.photos} />
          ))}
        </SectionCard>
      )}

      {grouped.product.length > 0 && (
        <SectionCard title="Products" count={grouped.product.length} icon={Package} color="blue">
          {grouped.product.map((r, i) => (
            <EntryCard key={r.id} idx={i} fields={[
              ['Product', r.product_name], ['Type', r.product_type], ['Quantity', r.quantity],
              ['Pricing', r.pricing], ['UDYAM / MSME', r.udyam_registration_no], ['Remarks', r.remarks],
            ]} photos={r.photos} />
          ))}
        </SectionCard>
      )}

      {grouped.marketing.length > 0 && (
        <SectionCard title="Marketing & Promotion" count={grouped.marketing.length} icon={Megaphone} color="purple">
          {grouped.marketing.map((r, i) => (
            <EntryCard key={r.id} idx={i} fields={[
              ['Activity', r.activity_name], ['Type', r.activity_type],
              ['Date', dateIN(r.event_date)], ['Description', r.description],
            ]} photos={r.photos} />
          ))}
        </SectionCard>
      )}

      {grouped.financial_support.length > 0 && (
        <SectionCard title="Financial Support" count={grouped.financial_support.length} icon={HandCoins} color="amber">
          {grouped.financial_support.map((r, i) => (
            <EntryCard key={r.id} idx={i} fields={[
              ['Institution', r.institution_name], ['Type', r.institution_type],
              ['Amount', r.support_amount ? `Rs. ${fmt(r.support_amount)}` : '-'],
              ['Date', dateIN(r.support_date)], ['Status', r.support_status],
            ]} photos={r.photos} />
          ))}
        </SectionCard>
      )}
    </div>
  );
};

const colorMap = {
  emerald: { border: 'border-emerald-500', bg: 'bg-emerald-700', text: 'text-emerald-700' },
  blue: { border: 'border-blue-500', bg: 'bg-blue-700', text: 'text-blue-700' },
  purple: { border: 'border-purple-500', bg: 'bg-purple-700', text: 'text-purple-700' },
  amber: { border: 'border-amber-500', bg: 'bg-amber-700', text: 'text-amber-700' },
};

const SectionCard = ({ title, count, icon: Icon, color, children }) => {
  const c = colorMap[color] || colorMap.emerald;
  return (
    <div className={`bg-white border-l-4 ${c.border} border border-slate-200 rounded-xl shadow-sm mb-4`}>
      <div className={`flex items-center justify-between px-4 py-3 border-b border-slate-100`}>
        <div className={`flex items-center gap-2 font-semibold ${c.text}`}><Icon className="w-4 h-4" />{title}</div>
        <span className="text-xs text-slate-500">{count} {count === 1 ? 'entry' : 'entries'}</span>
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
};

const EntryCard = ({ idx, fields, photos }) => (
  <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3">
    <div className="text-[11px] font-semibold text-slate-500 mb-2">Entry {idx + 1}</div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
      {fields.map(([k, v]) => (
        <div key={k} className="flex gap-2">
          <span className="text-slate-500 min-w-[130px]">{k}:</span>
          <span className="text-slate-900 font-medium">{v || <span className="text-slate-400 italic">-</span>}</span>
        </div>
      ))}
    </div>
    {photos && photos.length > 0 && (
      <div className="mt-3 pt-3 border-t border-slate-200">
        <div className="text-[11px] text-slate-500 mb-1.5">Activity Photographs</div>
        <div className="flex gap-2 flex-wrap">
          {photos.slice(0, 3).map((src, pi) => (
            <a key={pi} href={src} target="_blank" rel="noreferrer">
              <img src={src} alt={`Activity ${pi + 1}`} className="w-24 h-24 object-cover rounded border border-slate-300 hover:ring-2 hover:ring-emerald-500 cursor-zoom-in" />
            </a>
          ))}
        </div>
      </div>
    )}
  </div>
);

export default EntrepreneurLivelihoodsReport;
